import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// A merchant's change history (FIG-320 / REQ-012, REQ-025, REQ-039), read from
// Admin › User Activity rows that carry the merchant's id in their details.
// Most rows are written from the browser and can be missing for 28 Jul–Oct
// 2026, when browser logging was broken. Since FIG-345, Status changes and
// merchants created through onboarding are written server-side
// (supabase/functions/_shared/merchantHistory.ts).

export const MERCHANT_HISTORY_ACTIVITY_TYPES = [
  // FIG-345 UC-229: written server-side by update-merchant-report-sources-v1 (v2 since FIG-409).
  'merchant_report_source_change',
  'merchant_created',
  'merchant_type_change',
  'merchant_details_change',
  'merchant_status_change',
  // FIG-345 UC-228: written server-side by manage-merchant-contacts-v1.
  'merchant_contact_change',
] as const;

export type MerchantHistoryActivityType = (typeof MERCHANT_HISTORY_ACTIVITY_TYPES)[number];

export interface MerchantHistoryEntry {
  id: string;
  activityType: MerchantHistoryActivityType;
  text: string;
  by: string;
  at: string;
}

interface ActivityRow {
  id: string;
  user_email: string;
  activity_type: string;
  activity_details: Record<string, unknown> | null;
  created_at: string;
}

const TYPE_LABELS: Record<string, string> = { direct: 'Direct', reseller: 'Reseller' };

// Labels for Status history: the six statuses plus the pre-FIG-345 onboarding values.
const STATUS_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  pending: 'Pending',
  contracted: 'Contracted',
  watching: 'Watching',
  archived: 'Archived',
  test: 'Test',
  draft: 'Draft',
  submitted: 'Submitted',
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

export function describeHistoryEntry(row: Pick<ActivityRow, 'activity_type' | 'activity_details'>): string {
  const details = row.activity_details ?? {};
  switch (row.activity_type) {
    case 'merchant_report_source_change':
      return asString(details.summary) || 'Report data source changed';
    case 'merchant_created':
      return asString(details.summary) || 'Created';
    case 'merchant_type_change': {
      const from = asString(details.old_value);
      const to = asString(details.new_value);
      return `Type: ${TYPE_LABELS[from] ?? from} → ${TYPE_LABELS[to] ?? to}`;
    }
    case 'merchant_status_change': {
      const summary = asString(details.summary);
      if (summary) return summary;
      const from = asString(details.old_value);
      const to = asString(details.new_value);
      return `Status: ${STATUS_LABELS[from] ?? from} → ${STATUS_LABELS[to] ?? to}`;
    }
    case 'merchant_contact_change':
      return asString(details.summary) || 'Contacts changed';
    case 'merchant_details_change': {
      const fields = Array.isArray(details.changed_fields) ? details.changed_fields.filter((f) => typeof f === 'string') : [];
      return fields.length > 0 ? `Merchant details changed: ${fields.join(', ')}` : 'Merchant details changed';
    }
    default:
      return row.activity_type;
  }
}

export const merchantHistoryQueryKey = (organisationId: string) => ['merchant-history', organisationId] as const;

export async function fetchMerchantHistory(organisationId: string): Promise<MerchantHistoryEntry[]> {
  const { data, error } = await supabase
    .from('user_activity_logs')
    .select('id, user_email, activity_type, activity_details, created_at')
    .in('activity_type', [...MERCHANT_HISTORY_ACTIVITY_TYPES])
    .eq('activity_details->>organisation_id', organisationId)
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;

  return ((data ?? []) as ActivityRow[]).map((row) => ({
    id: row.id,
    activityType: row.activity_type as MerchantHistoryActivityType,
    text: describeHistoryEntry(row),
    by: row.user_email,
    at: row.created_at,
  }));
}

export function useMerchantHistory(organisationId: string | undefined) {
  return useQuery({
    queryKey: merchantHistoryQueryKey(organisationId ?? ''),
    queryFn: () => fetchMerchantHistory(organisationId as string),
    enabled: !!organisationId,
  });
}

/** Most recent entry of a given kind, for "Changed by … · date" lines. */
export function lastChangeOf(
  entries: readonly MerchantHistoryEntry[] | undefined,
  activityType: MerchantHistoryActivityType,
): MerchantHistoryEntry | undefined {
  return entries?.find((entry) => entry.activityType === activityType);
}
